import { AwardType } from '@occc-app/offerHelpers/types';
import { LimitTimePeriod, getAwardCountInitialValues } from '@occc/feat-limits';
import {
  FulfillmentChannel,
  getDefaultTimezone,
  getEmailRequiredInitialValues,
  getFulfillmentChannelInitialValues,
  getMinimumPurchaseConstraintInitialValues,
  getPaymentMethodInitialValues,
  getRequiresActivationWithLimitsInitialValues,
  transformAwardRestrictionsToFormValues,
  transformOfferToFulfillmentWindowFormValue,
} from '@occc/feat-constraints';
import {
  OfferFragment,
  EarningMechanismProductPurchaseFragment,
  EarningMechanismTotalSpendOverTimeFragment,
  EarningMechanismTotalSpendOnTransactionFragment,
  EarningMechanismTotalEligiblePurchaseFragment,
  EarningMechanismExternalFulfillmentFragment,
  Offer_Engagement_Type,
  ProductGroupFragment,
  Constraint_Purchase_Threshold_Counting_Attribute,
} from '@occc/graphql';
import { earningMechanismHelper } from '@occc-app/offerHelpers/utils/earningMechanismHelper';
import { awardHelper } from '@occc-app/offerHelpers/utils/awardHelper';
import {
  getProductPurchaseFormValues,
  getSpendBonusFormValues,
} from '@occc-app/offerHelpers/utils/transformOfferEarningMechanismToInitialValues';
import { transformOfferScanDataConfigurationToInitialValues } from '@occc-app/offerHelpers/utils/transformOfferScanDataConfigurationToInitialValues';
import { transformCustomData } from '@occc-app/routes/CampaignComponents/components/CustomData/utils';
import { getExternalFulfillmentAwardType } from '@occc-app/offerHelpers/utils/getExternalFulfillmentAwardType';
import { getOfferNameAndDescriptionInitialValues } from '@occc-app/routes/Offer/components/OfferConfigureForm/utils/offerNameAndDescriptionInitialValues';
import { getOfferCampaignInitialValues } from '@occc-app/routes/Offer/components/OfferConfigureForm/utils/offerCampaignInitialValues';
import { getOfferFulfillmentPriorityInitialValues } from '@occc-app/offerHelpers/utils/getOfferFulfillmentPriorityInitialValues';
import {
  OfferConfigureFormInitialValues,
  BaseEarnFormValues,
  BaseEligibleEarnFormValues,
  OfferType,
} from '../../../types';

export const TRANSACTION_FREQUENCY_DEFAULT = {
  isEnabled: false,
  rows: [{ limit: 1, timePeriod: 'DAY' as const }],
};

const getBaseEarnFormValues = (
  earningMechanism: EarningMechanismTotalSpendOnTransactionFragment,
): BaseEarnFormValues => {
  return {
    roundMoneyAmount: earningMechanism.roundMoneyAmount,
    ineligibleProductGroups: earningMechanism.ineligibleProductGroups,
  };
};

const getBaseEligibleEarnFormValues = (
  earningMechanism: EarningMechanismTotalEligiblePurchaseFragment,
): BaseEligibleEarnFormValues => {
  return {
    countProductsBy: earningMechanism.countProductsBy,
    roundCountedProducts: earningMechanism.roundCountedProducts,
    eligibleProductGroups: earningMechanism.eligibleProductGroups,
  };
};

const getProductPurchaseType = (
  award: EarningMechanismProductPurchaseFragment['productPurchaseAward'],
) => {
  if (awardHelper.isPointsAward(award)) {
    return AwardType.AwardPoints;
  }

  if (awardHelper.isVouchersAward(award)) {
    return AwardType.AwardVouchers;
  }

  return AwardType.AwardExternal;
};

const transformProductPurchase = (earningMechanism: EarningMechanismProductPurchaseFragment) => {
  return {
    productPurchase: getProductPurchaseFormValues(earningMechanism),
    awardType: getProductPurchaseType(earningMechanism.productPurchaseAward),
    awardPoints: awardHelper.isPointsAward(earningMechanism.productPurchaseAward)
      ? {
          points: earningMechanism.productPurchaseAward.points,
        }
      : {},
    awardVouchers: awardHelper.isVouchersAward(earningMechanism.productPurchaseAward)
      ? {
          voucheredOffer: earningMechanism.productPurchaseAward.voucheredOffer,
          voucherCount: earningMechanism.productPurchaseAward.voucherCount,
          voucherExpirationDays:
            earningMechanism.productPurchaseAward.voucherExpirationDays || undefined,
          isEnabled: !!earningMechanism.productPurchaseAward.voucherExpirationDays,
        }
      : {},
    awardExternal: awardHelper.isExternalAward(earningMechanism.productPurchaseAward)
      ? {
          provider: earningMechanism.productPurchaseAward.provider,
        }
      : {},
  };
};

const transformTotalSpend = (earningMechanism: EarningMechanismTotalSpendOverTimeFragment) => {
  return {
    spendBonus: getSpendBonusFormValues(earningMechanism),
    awardType: awardHelper.isPointsAward(earningMechanism.totalSpendOverTimeAward)
      ? AwardType.AwardPoints
      : AwardType.AwardVouchers,
    awardPoints: awardHelper.isPointsAward(earningMechanism.totalSpendOverTimeAward)
      ? {
          points: earningMechanism.totalSpendOverTimeAward.points,
        }
      : undefined,
    awardVouchers: awardHelper.isVouchersAward(earningMechanism.totalSpendOverTimeAward)
      ? {
          voucheredOffer: earningMechanism.totalSpendOverTimeAward.voucheredOffer,
          voucherCount: earningMechanism.totalSpendOverTimeAward.voucherCount,
          voucherExpirationDays:
            earningMechanism.totalSpendOverTimeAward.voucherExpirationDays || undefined,
          isEnabled: !!earningMechanism.totalSpendOverTimeAward.voucherExpirationDays,
        }
      : undefined,
  };
};

const transformTotalSpendOnTransaction = (
  earningMechanism: EarningMechanismTotalSpendOnTransactionFragment,
) => {
  return {
    baseEarn: getBaseEarnFormValues(earningMechanism),
    awardType: AwardType.AwardPointsPer,
    awardPointsPer: awardHelper.isPointsPerAward(earningMechanism.totalSpendOnTransactionAward)
      ? {
          points: earningMechanism.totalSpendOnTransactionAward.points,
          rounding: earningMechanism.totalSpendOnTransactionAward.rounding,
        }
      : undefined,
  };
};

const transformTotalEligiblePurchase = (
  earningMechanism: EarningMechanismTotalEligiblePurchaseFragment,
) => {
  return {
    baseEligibleEarn: getBaseEligibleEarnFormValues(earningMechanism),
    awardType: AwardType.AwardPointsPer,
    awardPointsPer: awardHelper.isPointsPerAward(earningMechanism.totalEligiblePurchaseAward)
      ? {
          points: earningMechanism.totalEligiblePurchaseAward.points,
          rounding: earningMechanism.totalEligiblePurchaseAward.rounding,
        }
      : undefined,
  };
};

const transformExternalFulfillment = (
  earningMechanism: EarningMechanismExternalFulfillmentFragment,
) => {
  return {
    externalFulfillment: earningMechanism.externalFulfillmentAward,
    awardType: getExternalFulfillmentAwardType(earningMechanism),
    awardPoints:
      earningMechanism.externalFulfillmentAward &&
      awardHelper.isPointsAward(earningMechanism.externalFulfillmentAward)
        ? {
            points: earningMechanism.externalFulfillmentAward.points,
          }
        : {},
    awardVouchers:
      earningMechanism.externalFulfillmentAward &&
      awardHelper.isVouchersAward(earningMechanism.externalFulfillmentAward)
        ? {
            voucheredOffer: earningMechanism.externalFulfillmentAward.voucheredOffer,
            voucherCount: earningMechanism.externalFulfillmentAward.voucherCount,
            voucherExpirationDays:
              earningMechanism.externalFulfillmentAward.voucherExpirationDays || undefined,
            isEnabled: !!earningMechanism.externalFulfillmentAward.voucherExpirationDays,
          }
        : {},
  };
};

const transformOfferEarningMechanism = (earningMechanism: OfferFragment['earningMechanism']) => {
  if (!earningMechanism) {
    return {};
  }

  if (earningMechanismHelper.isProductPurchase(earningMechanism)) {
    return {
      offerType: OfferType.ProductPurchase,
      ...transformProductPurchase(earningMechanism),
    };
  }

  if (earningMechanismHelper.isTotalSpendOverTime(earningMechanism)) {
    return {
      offerType: OfferType.SpendBonus,
      ...transformTotalSpend(earningMechanism),
    };
  }

  if (earningMechanismHelper.isTotalSpendOnTransaction(earningMechanism)) {
    return {
      offerType: OfferType.BaseEarn,
      ...transformTotalSpendOnTransaction(earningMechanism),
    };
  }

  if (earningMechanismHelper.isTotalEligiblePurchase(earningMechanism)) {
    return {
      offerType: OfferType.BaseEligibleEarn,
      ...transformTotalEligiblePurchase(earningMechanism),
    };
  }

  if (earningMechanismHelper.isExternalFulfillment(earningMechanism)) {
    return {
      offerType: OfferType.ExternalFulfillment,
      ...transformExternalFulfillment(earningMechanism),
    };
  }

  return {};
};

export const getOfferConfigureFormInitialValues = (): OfferConfigureFormInitialValues => ({
  name: '',
  offerType: undefined,
  earningMechanism: {},
  awardType: AwardType.AwardPoints,
  awardVouchers: {
    isEnabled: false,
    voucherCount: 1,
  },
  awardCount: {
    isEnabled: false,
    global: {
      isEnabled: false,
      timePeriod: LimitTimePeriod.Total,
    },
    membership: {
      isEnabled: false,
      timePeriod: LimitTimePeriod.Total,
    },
    transaction: {
      isEnabled: false,
    },
    transactionFrequency: TRANSACTION_FREQUENCY_DEFAULT,
  },
  activation: {
    isEnabled: false,
    global: {
      isEnabled: false,
      timePeriod: LimitTimePeriod.Total,
    },
    membership: {
      isEnabled: false,
      timePeriod: LimitTimePeriod.Total,
    },
    requiresActivation: {
      isMustFulfillWithinEnabled: false,
      allowMultipleFulfillmentsOnActivation: false,
    },
  },
  fulfillmentWindow: {
    isEnabled: false,
    timezone: getDefaultTimezone(),
  },
  minimumPurchaseConstraints: {
    isEnabled: false,
    items: [
      {
        countProductsBy: Constraint_Purchase_Threshold_Counting_Attribute.Quantity,
      },
    ],
  },
  paymentMethodConstraint: {
    isEnabled: false,
  },
  emailRequiredConstraint: {
    isEnabled: false,
  },
  fulfillmentChannelConstraint: {
    isEnabled: false,
    channel: FulfillmentChannel.OrderAheadOnly,
  },
  engagementType: Offer_Engagement_Type.Transactional,
  awardRestrictions: { isEnabled: false, awards: [] },
  scanDataConfiguration: {
    isEnabled: false,
    fundingSource: '',
    description: '',
    discountAllocation: undefined,
  },
});

const getActivationInitialValues = (
  offer: OfferFragment,
  defaultValues?: OfferConfigureFormInitialValues['activation'],
): NonNullable<OfferConfigureFormInitialValues['activation']> => {
  // Shared feat-constraints helper returns broad form-state values; Offer schema narrows by `isEnabled`.
  return getRequiresActivationWithLimitsInitialValues(
    offer,
    defaultValues ?? getOfferConfigureFormInitialValues().activation,
  ) as NonNullable<OfferConfigureFormInitialValues['activation']>;
};

const getAwardCountSchemaInitialValues = (
  offer: OfferFragment,
  defaultValues?: OfferConfigureFormInitialValues['awardCount'],
): NonNullable<OfferConfigureFormInitialValues['awardCount']> =>
  getAwardCountInitialValues(offer, defaultValues) as NonNullable<
    OfferConfigureFormInitialValues['awardCount']
  >;

export const transformOfferToInitialValues = (
  offer?: OfferFragment,
  productGroups?: ProductGroupFragment[],
): OfferConfigureFormInitialValues => {
  const values = getOfferConfigureFormInitialValues();

  if (!offer) {
    return values;
  }

  const awardRestrictionsConstraint = offer.constraints.find(
    (constraint) => constraint.__typename === 'ConstraintPurchasableEligibility',
  );

  return {
    ...values,
    ...getOfferNameAndDescriptionInitialValues(offer),
    ...getOfferCampaignInitialValues(offer),
    ...getOfferFulfillmentPriorityInitialValues(offer),
    customData: transformCustomData(offer.customData),
    earningMechanism: {
      id: offer.earningMechanism?.id,
    },
    ...transformOfferEarningMechanism(offer.earningMechanism),
    ...getMinimumPurchaseConstraintInitialValues(offer, productGroups),
    activation: getActivationInitialValues(offer, values.activation),
    awardCount: getAwardCountSchemaInitialValues(offer, values.awardCount),
    ...transformOfferToFulfillmentWindowFormValue(offer),
    paymentMethodConstraint: getPaymentMethodInitialValues(offer),
    emailRequiredConstraint: getEmailRequiredInitialValues(offer),
    fulfillmentChannelConstraint: getFulfillmentChannelInitialValues(offer),
    engagementType: offer.engagementType,
    awardRestrictions: awardRestrictionsConstraint
      ? transformAwardRestrictionsToFormValues(awardRestrictionsConstraint)
      : values.awardRestrictions,
    ...transformOfferScanDataConfigurationToInitialValues(offer.scanDataConfiguration),
  };
};
